import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { usePricing } from '@/hooks/usePricing';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Info } from 'lucide-react';
import { toast } from 'sonner';

const HANDLING_FEE = 15;
const PLATFORM_SHIPPING_MARGIN = 15;

export const CartPage = () => {
  const { cart, updateCartItem, removeFromCart, loading } = useCart();
  const { isAuthenticated } = useAuth();
  const { isBusiness, displayPrice, vatAmount, vatLabel, priceExclVat } = usePricing();
  const navigate = useNavigate();

  const handleQuantityChange = async (productId, newQuantity) => {
    try { await updateCartItem(productId, newQuantity); } catch { toast.error('Kunde inte uppdatera antal'); }
  };
  const handleRemove = async (productId) => {
    try { await removeFromCart(productId); toast.success('Produkten har tagits bort'); } catch { toast.error('Kunde inte ta bort produkten'); }
  };
  const handleCheckout = () => {
    if (!isAuthenticated) { toast.error('Du måste vara inloggad för att gå till kassan'); navigate('/logga-in'); return; }
    navigate('/kassa');
  };

  const subtotal = cart.total || cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const shippingTotal = cart.shipping_total || 0;
  const platformShippingMargin = cart.items.length > 0 ? PLATFORM_SHIPPING_MARGIN : 0;
  const handlingFee = cart.items.length > 0 ? HANDLING_FEE : 0;
  const grandTotal = subtotal + shippingTotal + platformShippingMargin + handlingFee;
  const totalVat = vatAmount(grandTotal);

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="cart-page">
      <div className="container-main py-8">
        <Breadcrumbs items={[{ label: 'Varukorg' }]} />
        <h1 className="text-2xl md:text-3xl font-black text-[#0F172A] mb-8">Varukorg</h1>

        {cart.items.length === 0 ? (
          <div className="bg-white rounded-md shadow-sm p-12 text-center">
            <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#0F172A] mb-2">Din varukorg är tom</h2>
            <p className="text-gray-500 mb-6">Börja handla för att fylla din varukorg</p>
            <Link to="/produkter"><Button className="bg-[#FF8A00] hover:bg-[#e67c00]">Utforska produkter</Button></Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item) => (
                <div key={item.product_id} className="bg-white rounded-md shadow-sm p-4 flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                  <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                    {item.image ? <img src={item.image} alt={item.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-400"><ShoppingBag className="h-8 w-8" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/produkt/${item.product_id}`} className="font-semibold text-[#0F172A] hover:text-[#003B73] line-clamp-2">{item.title}</Link>
                    {item.store_name && <p className="text-sm text-gray-500">{item.store_name}</p>}
                    <p className="text-sm text-gray-500 mt-1">Frakt: {item.shipping_cost > 0 ? `${item.shipping_cost} kr` : 'Gratis'}</p>
                    <div className="mt-2">
                      <p className="font-bold text-[#003B73]">{displayPrice(item.price).toLocaleString('sv-SE')} kr</p>
                      <p className="text-[10px] text-gray-400">{vatLabel}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button onClick={() => handleRemove(item.product_id)} className="text-gray-400 hover:text-red-500 transition-colors" data-testid={`remove-item-${item.product_id}`}>
                      <Trash2 className="h-5 w-5" />
                    </button>
                    <div className="flex items-center border rounded-sm">
                      <button onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)} className="p-2 hover:bg-gray-100" disabled={loading} data-testid={`decrease-${item.product_id}`}><Minus className="h-4 w-4" /></button>
                      <span className="px-3 font-semibold">{item.quantity}</span>
                      <button onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)} className="p-2 hover:bg-gray-100" disabled={loading} data-testid={`increase-${item.product_id}`}><Plus className="h-4 w-4" /></button>
                    </div>
                    <p className="font-bold text-[#0F172A]">{displayPrice(item.item_total || item.price * item.quantity).toLocaleString('sv-SE')} kr</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-md shadow-sm p-6 sticky top-24" data-testid="order-summary">
                <h2 className="text-lg font-bold text-[#0F172A] mb-6">Sammanfattning</h2>

                <div className="space-y-3 text-sm">
                  {isBusiness ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Produkter exkl. moms ({cart.items.length} st)</span>
                        <span className="font-semibold">{priceExclVat(subtotal).toLocaleString('sv-SE')} kr</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Frakt exkl. moms</span>
                        <span className="font-semibold">{priceExclVat(shippingTotal).toLocaleString('sv-SE')} kr</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Expeditionsavgift exkl. moms</span>
                        <span className="font-semibold">{priceExclVat(handlingFee).toLocaleString('sv-SE')} kr</span>
                      </div>
                      <div className="border-t border-gray-100 pt-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-medium">Summa exkl. moms</span>
                          <span className="font-bold">{priceExclVat(grandTotal).toLocaleString('sv-SE')} kr</span>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Moms (25%)</span>
                        <span className="font-semibold">{totalVat.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Produkter ({cart.items.length} st)</span>
                        <span className="font-semibold">{subtotal.toLocaleString('sv-SE')} kr</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Frakt</span>
                        <span className="font-semibold">{shippingTotal.toLocaleString('sv-SE')} kr</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Expeditionsavgift</span>
                        <span className="font-semibold">{handlingFee} kr</span>
                      </div>
                      {/* VAT Info */}
                      <div className="bg-blue-50 rounded-md p-3 mt-1">
                        <div className="flex items-start gap-2">
                          <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="text-xs text-blue-800">
                            <p className="font-semibold">Varav moms (25%)</p>
                            <p>{totalVat.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr</p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="border-t border-gray-100 mt-4 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-[#0F172A]">Totalt</span>
                    <span className="text-2xl font-black text-[#003B73]" data-testid="cart-total">
                      {grandTotal.toLocaleString('sv-SE')} kr
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{isBusiness ? 'Inkl. moms' : 'Inkl. moms'}</p>
                </div>

                <Button onClick={handleCheckout} className="w-full mt-6 h-14 bg-[#FF8A00] hover:bg-[#e67c00] text-white font-bold text-lg rounded-sm" data-testid="checkout-btn">
                  Till kassan <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Link to="/produkter" className="block text-center mt-4">
                  <Button variant="ghost" className="text-[#003B73]">Fortsätt handla</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
